/* eslint-disable max-lines */
// TODO This file should be cut by pieces by @skokalin
import React, {Component, ChangeEvent, FormEvent} from 'react';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {AxiosPromise} from 'axios';

import {ActionCreatorType, ActionType} from '@typescript/common/actionTypes';
import {ApplicationStoreType} from '@typescript/common/storeTypes';

import {Input} from '@optimaxdev/design-system';
import Error from 'components/__helpers/error/error';
import Checkbox from 'components/__legacy/checkbox/checkbox';
import {PrivacyPolicy} from 'components/privacyPolicy/privacyPolicy';
import {ValidationAuthorizationRegister} from 'libs/validation/validationField';
import {sendRegistration} from 'actions/authorization/authorization';
import {setCustomerEmail} from 'reducers/analytics/dataLayer';
import {getCustomer, getToken} from 'reducers/oAuth/oAuth';
import {TermsOfUse} from 'components/termsOfUse/termsOfUse';

import s from './register.scss';
import {sendAnalytics} from 'features/wizardMs/utils/sendAnalytics';
import {LOGIN} from '@optimaxdev/analytics/mobile';
import {
    getPasswordRequirements,
    PasswordRequirements,
} from './passwordRequirements/passwordRequirements';

type RegDataType = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    isAgreedPrivacyPolicy: boolean;
    isSubscribed: boolean;
};

export type RequirementDataType = {
    touched: boolean;
    hasMinChars: boolean;
    hasRepeatedChars: boolean;
};

type PropsType = {
    formKey: string;
    error: string;
    sendRegistration: (arg1: RegDataType, arg2: string) => ActionType<AxiosPromise>;
    setCustomerEmail: ActionCreatorType<string>;
    isStrictPrivacy: boolean;
    getToken: (email: string, password: string, type: 'signup') => void;
};

type StateType = {
    errors: {
        firstName: string;
        lastName: string;
        email: string;
        message: string;
        isAgreedPrivacyPolicy: string;
        isSubscribed: string;
    };
    regData: RegDataType;
    passwordRequirementsData: RequirementDataType;
    isPasswordRequirementsVisible: boolean;
};

export const EMPTY_ERRORS = {
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    isAgreedPrivacyPolicy: '',
    isSubscribed: '',
};

const INPUT_LABELS = {
    firstName: 'Name',
    lastName: 'Last Name',
    email: 'Your Email Address',
    password: 'Password',
    confirmation: 'Confirm Password',
};

/**
 * Register authorization component
 * @param {StateType} state - component state
 */
export class Register extends Component<PropsType, StateType> {
    state = {
        errors: {...EMPTY_ERRORS},
        regData: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            isAgreedPrivacyPolicy: !this.props.isStrictPrivacy,
            isSubscribed: !this.props.isStrictPrivacy,
        },
        passwordRequirementsData: {
            touched: false,
            hasMinChars: false,
            hasRepeatedChars: false,
        },
        isPasswordRequirementsVisible: false,
    };

    /**
     * Change handler input
     * @param {string} name - field name
     * @param {string} value - error
     */
    setError = (name: string, value: string) => {
        this.setState(({errors}) => ({errors: {...errors, [name]: value}}));
    };

    /**
     * Change handler input
     * @param {ChangeEvent<HTMLInputElement>} event - Event object
     */
    changeHandler = ({target: {name, value, type, checked}}: ChangeEvent<HTMLInputElement>) => {
        const passwordRequirements = name === 'password' ? getPasswordRequirements(value) : null;

        this.setState(({regData, errors, passwordRequirementsData}) => ({
            regData: {...regData, [name]: type === 'checkbox' ? checked : value},
            errors: {...errors, [name]: ''},
            passwordRequirementsData: passwordRequirements || passwordRequirementsData,
        }));
    };

    /**
     * Logs a user or returns an error
     */
    sendRegistration = () => {
        const {regData} = this.state;
        const validation = new ValidationAuthorizationRegister(regData).all();
        const wrongFields = Object.keys(validation).filter(
            field => validation[field] !== 'success',
        );

        if (wrongFields.length === 0) {
            this.props
                .sendRegistration(regData, this.props.formKey)
                // @ts-ignore
                .then(() => {
                    this.props.getToken(regData.email, regData.password, 'signup');
                    sendAnalytics(LOGIN.NEWSLETTER, {
                        socialNetwork: 'Email',
                        label: regData.isSubscribed ? 'Subscribed' : 'Unsubscribed',
                    });
                })
                .catch(
                    ({
                        response: {
                            data: {messages},
                        },
                    }: {
                        response: {data: {messages: string}};
                    }) => {
                        sendAnalytics(LOGIN.EMAIL_VALUE_SIGNUP, {
                            email: 'Email',
                            label: `Error - ${messages}`,
                        });
                        this.setError('message', messages);
                    },
                );
        } else {
            wrongFields.forEach(field => this.setError(field, validation[field]));
        }
    };

    /**
     * Function handle for submit
     * @param {FormEvent<HTMLFormElement>} event - JS event
     */
    handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.setState({errors: {...EMPTY_ERRORS}});
        this.sendRegistration();
    };

    togglePasswordRequirements = (state: boolean) => {
        this.setState({isPasswordRequirementsVisible: state});
    };

    /**
     * Render jsx to html
     * @returns {Node} Rendered react component
     */
    render() {
        const {
            regData,
            errors,
            passwordRequirementsData,
            isPasswordRequirementsVisible,
        } = this.state;
        const {error} = this.props;
        return (
            <div>
                <div className={s.methods}>
                    <form onSubmit={this.handleSubmit} data-test-name="registrationForm">
                        <div className={s.half}>
                            {['firstName', 'lastName'].map(field => (
                                <div className={s.inputBlock} data-test-name={field} key={field}>
                                    <Input
                                        onChange={this.changeHandler}
                                        isRequired
                                        message={errors[field]}
                                        isError={Boolean(errors[field])}
                                        value={regData[field]}
                                        label={INPUT_LABELS[field]}
                                        name={field}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className={s.inputBlock} data-test-name="email">
                            <Input
                                type="email"
                                onChange={this.changeHandler}
                                message={errors.email}
                                isError={Boolean(errors.email)}
                                isRequired
                                value={regData.email}
                                label={INPUT_LABELS.email}
                                name="email"
                                onBlur={({target}) => this.props.setCustomerEmail(target.value)}
                            />
                        </div>

                        <div className={s.inputBlock} data-test-name="password">
                            <Input
                                type="password"
                                isRequired
                                onChange={this.changeHandler}
                                value={regData.password}
                                label={INPUT_LABELS.password}
                                name="password"
                                onFocus={() => this.togglePasswordRequirements(true)}
                            />

                            {isPasswordRequirementsVisible && (
                                <div className={s.passwordRequirementsWrapper}>
                                    <PasswordRequirements
                                        requirementsData={passwordRequirementsData}
                                    />
                                </div>
                            )}
                        </div>

                        {this.props.isStrictPrivacy && (
                            <>
                                <div
                                    className={classNames(s.checkbox, {
                                        [s.error]: errors.isAgreedPrivacyPolicy,
                                    })}
                                    data-test-name="privacy"
                                >
                                    <Checkbox
                                        name="isAgreedPrivacyPolicy"
                                        value={regData.isAgreedPrivacyPolicy}
                                        handleChange={this.changeHandler}
                                        fontSize="14px"
                                        lineHeight="14px"
                                    >
                                        I agree to the{' '}
                                        <PrivacyPolicy>
                                            <span className={s.privacyPolicy}>Privacy Policy</span>
                                        </PrivacyPolicy>
                                    </Checkbox>
                                    {errors.isAgreedPrivacyPolicy && (
                                        <p className={s.error}>{errors.isAgreedPrivacyPolicy}</p>
                                    )}
                                </div>
                                <div className={s.checkbox}>
                                    <Checkbox
                                        name="isSubscribed"
                                        value={regData.isSubscribed}
                                        handleChange={this.changeHandler}
                                        fontSize="14px"
                                        lineHeight="14px"
                                    >
                                        Sign up for newsletter
                                    </Checkbox>
                                </div>
                            </>
                        )}
                        <button
                            type="submit"
                            className={s.submit}
                            data-test-name="submit"
                            disabled={
                                passwordRequirementsData.hasRepeatedChars ||
                                !passwordRequirementsData.hasMinChars
                            }
                        >
                            Sign Up
                        </button>
                        {!this.props.isStrictPrivacy && (
                            <p className={s.notification}>
                                By signing up you agree to the{' '}
                                <PrivacyPolicy>
                                    <span className={s.privacyPolicy}>Privacy Policy</span>
                                </PrivacyPolicy>{' '}
                                & <TermsOfUse />
                            </p>
                        )}
                        <Error error={errors.message || error} />
                        {!this.props.isStrictPrivacy && (
                            <div className={s.checkbox}>
                                <Checkbox
                                    name="isSubscribed"
                                    value={regData.isSubscribed}
                                    handleChange={this.changeHandler}
                                    fontSize="14px"
                                    lineHeight="14px"
                                    checked={regData.isSubscribed}
                                >
                                    Sign up for newsletter
                                </Checkbox>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        );
    }
}

/**
 * Map state to props
 * @param {ApplicationStoreType} state - redux store
 * @returns {PropsType} props
 */
export const mapStateToProps = (state: ApplicationStoreType) => ({
    isStrictPrivacy: state.customer.strictPrivacy,
    error: getCustomer(state).error || '',
});

export const mapDispatchToProps = {
    setCustomerEmail,
    sendRegistration,
    getToken,
};

export const RegisterConnected = connect(mapStateToProps, mapDispatchToProps)(Register);
